import { Injectable } from "@angular/core";
import axios from 'axios';
import { EMPTY, Observable, from, throwError } from "rxjs";
import { map, switchMap, catchError } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { ENV } from '../environment/environment'
import { Storage } from '@ionic/storage';
import { IHttpRequest } from "./IHttpRequest ";

@Injectable({
  providedIn: "root",
})
export class ExternalAxiosHttpRequest implements IHttpRequest {
 
  constructor(
    private authService: MsalService
    , private storage: Storage
  ) {
  }

  public get<T = any>(url: string, params?: any): Observable<T> {

    if (!Promise.prototype.finally) {
      Promise.prototype.finally = function (onFinally) {
          return this.then(
              value => Promise.resolve(onFinally()).then(() => value),
              reason => Promise.resolve(onFinally()).then(() => { throw reason })
          );
      };
    }

    return this.authService.acquireTokenSilent({ scopes: ENV.apiConfig.scopes }).pipe(
      switchMap(token => {
        const config = {
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
            'Content-Type': 'application/json'
          },
          params: params
        };
  
        return from(axios.get(url, config));
      })
      , map(response => response.data)
      , catchError(
        error => {
          return this.handleAxiosError(error);
        }
      )
    );
  }

  public post<T = any>(url: string, body: any): Observable<T> {

    if (!Promise.prototype.finally) {
      Promise.prototype.finally = function (onFinally) {
          return this.then(
              value => Promise.resolve(onFinally()).then(() => value),
              reason => Promise.resolve(onFinally()).then(() => { throw reason })
          );
      };
    }
    
    return this.authService.acquireTokenSilent({ scopes: ENV.apiConfig.scopes }).pipe(
      switchMap(token => {
        const config = {
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
            'Content-Type': 'application/json'
          }
        };
  
        return from(axios.post(url, body, config));
      })
      , map(response => response.data)
      , catchError(
        error => {
          return this.handleAxiosError(error);
        }
      )
    );
  }

  public postWithHeaderParams<T = any>(url: string, body: any, params: any): Observable<T> {
    if (!Promise.prototype.finally) {
      Promise.prototype.finally = function (onFinally) {
          return this.then(
              value => Promise.resolve(onFinally()).then(() => value),
              reason => Promise.resolve(onFinally()).then(() => { throw reason })
          );
      };
    }
    
    return this.authService.acquireTokenSilent({ scopes: ENV.apiConfig.scopes }).pipe(
      switchMap(token => {
        const config = {
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
            'Content-Type': 'application/json'
          }
          , params: params
        };
  
        return from(axios.post(url, body, config));
      })
      , map(response => response.data)
      , catchError(
        error => {
          return this.handleAxiosError(error);
        }
      )
    );
  }

  private handleAxiosError(error: any) {
    // Check for token expiration error
    const errorMessage = error?.response?.data?.error || error?.message;
    const errorName = error?.name;

    if (
      errorMessage?.includes('invalid_token')
      || errorMessage?.includes('interaction_required')
      || errorName?.includes('BrowserAuthError')
    ) {
      console.info('Token expired or invalid, clearing storage and reloading...');
      this.storage.clear();
      this.authService.loginRedirect({
        scopes: ENV.apiConfig.scopes
      });
      return EMPTY;
    } else {
      const status = error?.response?.status;
      const data = error?.response?.data;

      const simpleError = {
        status: status || 500,
        message: data?.message || data?.error || 'Internal Server Error',
        _body: typeof data === 'string' ? data : JSON.stringify(data)
      };

      // Let non-token-related errors propagate to subscriber
      return throwError(simpleError);
    }
  }
}



// WEBPACK FOOTER //
// ./src/infra/externalAxiosHttpRequest.ts